import { supabase } from '../config/supabase';
import {
  Engineer,
  EngineerSkill,
  EngineerAvailability,
  Project,
  ProjectSkill,
  ProjectAssignment,
  EngineerWithSkills,
  ProjectWithSkills,
  CreateEngineerRequest,
  CreateProjectRequest,
  ApiResponse,
  MatchingResult,
  MatchingFilters
} from '../types';

// Engineers Service
export const engineersService = {
  // Get all engineers with their skills
  async getAll(): Promise<ApiResponse<EngineerWithSkills[]>> {
    try {
      const { data: engineers, error: engineersError } = await supabase
        .from('engineers')
        .select(`
          *,
          engineer_skills (*),
          engineer_availability (*)
        `)
        .order('created_at', { ascending: false });

      if (engineersError) {
        return { data: null, error: engineersError.message };
      }

      interface SupabaseEngineerResponse {
        id: number;
        name: string;
        email?: string;
        phone?: string;
        age?: number;
        gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
        nearest_station?: string;
        address?: string;
        education?: string;
        experience_years?: number;
        hourly_rate?: number;
        availability_status: 'available' | 'busy' | 'unavailable';
        location?: string;
        remote_work_preference: boolean;
        bio?: string;
        created_at: string;
        updated_at: string;
        engineer_skills?: EngineerSkill[];
        engineer_availability?: EngineerAvailability[];
      }

      const engineersWithSkills: EngineerWithSkills[] = engineers?.map((engineer: SupabaseEngineerResponse) => ({
        ...engineer,
        skills: engineer.engineer_skills || [],
        availability: engineer.engineer_availability || []
      })) || [];

      return { data: engineersWithSkills, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Get engineer by ID
  async getById(id: number): Promise<ApiResponse<EngineerWithSkills>> {
    try {
      const { data: engineer, error } = await supabase
        .from('engineers')
        .select(`
          *,
          engineer_skills (*),
          engineer_availability (*)
        `)
        .eq('id', id)
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      const engineerWithSkills: EngineerWithSkills = {
        ...engineer,
        skills: engineer.engineer_skills || [],
        availability: engineer.engineer_availability || []
      };

      return { data: engineerWithSkills, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Create new engineer
  async create(engineerData: CreateEngineerRequest): Promise<ApiResponse<Engineer>> {
    try {
      const { skills, ...engineerInfo } = engineerData;

      // Create engineer
      const { data: engineer, error: engineerError } = await supabase
        .from('engineers')
        .insert([engineerInfo])
        .select()
        .single();

      if (engineerError) {
        return { data: null, error: engineerError.message };
      }

      // Create skills if provided
      if (skills && skills.length > 0) {
        
        // スキルデータのバリデーション
        const validSkills = skills.filter(skill => {
          if (!skill.skill_name || skill.skill_name.trim() === '') {
            console.warn('空のスキル名をスキップ:', skill);
            return false;
          }
          if (skill.skill_name.length > 100) {
            console.warn('スキル名が長すぎるためスキップ:', skill.skill_name);
            return false;
          }
          if (!['beginner', 'intermediate', 'advanced', 'expert'].includes(skill.skill_level)) {
            console.warn('無効なスキルレベルをデフォルトに変更:', skill.skill_level, '-> beginner');
            skill.skill_level = 'beginner';
          }
          if (skill.years_experience && (skill.years_experience < 0 || skill.years_experience > 50)) {
            console.warn('無効な経験年数を0に変更:', skill.years_experience, '-> 0');
            skill.years_experience = 0;
          }
          return true;
        });

        if (validSkills.length === 0) {
          return { data: engineer, error: null };
        }
        
        const skillsWithEngineerId = validSkills.map(skill => ({
          engineer_id: engineer.id,
          skill_name: skill.skill_name.trim(),
          skill_level: skill.skill_level || 'beginner',
          years_experience: skill.years_experience ? Math.round(skill.years_experience) : 0
        }));


        const { error: skillsError } = await supabase
          .from('engineer_skills')
          .insert(skillsWithEngineerId);

        if (skillsError) {
          console.error('=== スキル作成エラー詳細 ===');
          console.error('エラーコード:', skillsError.code);
          console.error('エラーメッセージ:', skillsError.message);
          console.error('エラー詳細:', skillsError.details);
          console.error('挿入しようとしたデータ:', JSON.stringify(skillsWithEngineerId, null, 2));
          console.error('========================');
          
          // スキル作成に失敗した場合、エンジニアを削除してエラーを返す
          await supabase
            .from('engineers')
            .delete()
            .eq('id', engineer.id);
          
          return { data: null, error: `スキルの登録に失敗しました: ${skillsError.message}` };
        }
      }

      return { data: engineer, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Update engineer
  async update(id: number, engineerData: Partial<Engineer>): Promise<ApiResponse<Engineer>> {
    try {
      const { data: engineer, error } = await supabase
        .from('engineers')
        .update(engineerData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: engineer, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Delete engineer
  async delete(id: number): Promise<ApiResponse<boolean>> {
    try {
      const { error } = await supabase
        .from('engineers')
        .delete()
        .eq('id', id);

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: true, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },


  // Add skill to engineer
  async addSkill(engineerId: number, skill: Omit<EngineerSkill, 'id' | 'engineer_id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<EngineerSkill>> {
    try {
      const { data, error } = await supabase
        .from('engineer_skills')
        .insert([{ ...skill, engineer_id: engineerId }])
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Update skill
  async updateSkill(skillId: number, skill: Partial<EngineerSkill>): Promise<ApiResponse<EngineerSkill>> {
    try {
      const { data, error } = await supabase
        .from('engineer_skills')
        .update(skill)
        .eq('id', skillId)
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Delete skill from engineer
  async deleteSkill(skillId: number): Promise<ApiResponse<boolean>> {
    try {
      const { error } = await supabase
        .from('engineer_skills')
        .delete()
        .eq('id', skillId);

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: true, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Remove skill from engineer (alias for deleteSkill for backward compatibility)
  async removeSkill(skillId: number): Promise<ApiResponse<boolean>> {
    return this.deleteSkill(skillId);
  },

  // Save project history for engineer
  async saveProjectHistory(engineerId: number, projects: Array<{
    projectName: string;
    description?: string;
    startDate?: string;
    endDate?: string;
    technologies?: string[];
    role?: string;
    confidence?: number;
  }>): Promise<ApiResponse<boolean>> {
    try {
      const projectsWithEngineerId = projects.map(project => ({
        engineer_id: engineerId,
        project_name: project.projectName,
        project_description: project.description,
        start_date: project.startDate,
        end_date: project.endDate,
        technologies: project.technologies,
        role: project.role,
        is_calculated: true,
        confidence_score: project.confidence || 1.0,
        source_type: 'excel_import'
      }));

      const { error } = await supabase
        .from('engineer_project_history')
        .insert(projectsWithEngineerId);

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: true, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Save technology experience for engineer
  async saveTechnologyExperience(engineerId: number, technologies: Record<string, {
    technology: string;
    totalYears: number;
    firstUsed?: string;
    lastUsed?: string;
    projects: any[];
    confidence: number;
  }>): Promise<ApiResponse<boolean>> {
    try {
      const techExperiences = Object.values(technologies).map((tech) => ({
        engineer_id: engineerId,
        technology_name: tech.technology,
        total_experience_years: tech.totalYears,
        total_experience_months: Math.round(tech.totalYears * 12),
        first_used_date: tech.firstUsed,
        last_used_date: tech.lastUsed,
        project_count: tech.projects.length,
        confidence_score: tech.confidence,
        calculation_method: 'auto',
        last_calculated_at: new Date()
      }));

      // 既存の技術経験データを削除（自動計算のもののみ）
      await supabase
        .from('engineer_technology_experience')
        .delete()
        .eq('engineer_id', engineerId)
        .eq('calculation_method', 'auto');

      // 新しい技術経験データを挿入
      const { error } = await supabase
        .from('engineer_technology_experience')
        .insert(techExperiences);

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: true, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Save import history
  async saveImportHistory(engineerId: number, importData: {
    fileName: string;
    fileType: string;
    technologiesFound?: string[];
    projectsFound?: number;
    confidence?: number;
    insights?: string[];
    warnings?: string[];
    rawData?: Record<string, unknown>;
  }): Promise<ApiResponse<boolean>> {
    try {
      const importHistory = {
        engineer_id: engineerId,
        file_name: importData.fileName,
        file_type: importData.fileType,
        import_method: 'ai_assisted',
        technologies_found: importData.technologiesFound || [],
        projects_found: importData.projectsFound || 0,
        confidence_score: importData.confidence,
        ai_insights: importData.insights || [],
        warnings: importData.warnings || [],
        raw_data: importData.rawData || {}
      };

      const { error } = await supabase
        .from('skill_sheet_imports')
        .insert([importHistory]);

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: true, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  }
};

// Projects Service
export const projectsService = {
  // Get all projects with their skills
  async getAll(): Promise<ApiResponse<ProjectWithSkills[]>> {
    try {
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select(`
          *,
          project_skills (*),
          project_assignments (*)
        `)
        .order('created_at', { ascending: false });

      if (projectsError) {
        return { data: null, error: projectsError.message };
      }

      interface SupabaseProjectResponse {
        id: number;
        title: string;
        description?: string;
        client_name?: string;
        budget?: number;
        start_date?: string;
        end_date?: string;
        status: 'pending' | 'active' | 'completed' | 'cancelled';
        priority: 'low' | 'medium' | 'high' | 'urgent';
        location?: string;
        remote_work: boolean;
        line_message_id?: string;
        created_at: string;
        updated_at: string;
        project_skills?: ProjectSkill[];
        project_assignments?: ProjectAssignment[];
      }

      const projectsWithSkills: ProjectWithSkills[] = projects?.map((project: SupabaseProjectResponse) => ({
        ...project,
        skills: project.project_skills || [],
        assignments: project.project_assignments || []
      })) || [];

      return { data: projectsWithSkills, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Get project by ID
  async getById(id: number): Promise<ApiResponse<ProjectWithSkills>> {
    try {
      const { data: project, error } = await supabase
        .from('projects')
        .select(`
          *,
          project_skills (*),
          project_assignments (*)
        `)
        .eq('id', id)
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      const projectWithSkills: ProjectWithSkills = {
        ...project,
        skills: project.project_skills || [],
        assignments: project.project_assignments || []
      };

      return { data: projectWithSkills, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Create new project
  async create(projectData: CreateProjectRequest): Promise<ApiResponse<Project>> {
    try {
      const { skills, ...projectInfo } = projectData;

      // Create project
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .insert([projectInfo])
        .select()
        .single();

      if (projectError) {
        return { data: null, error: projectError.message };
      }

      // Create skills if provided
      if (skills && skills.length > 0) {
        const skillsWithProjectId = skills.map(skill => ({
          skill_name: skill.skill_name,
          required_years: skill.required_years,
          is_required: skill.is_required,
          project_id: project.id
        }));

        const { error: skillsError } = await supabase
          .from('project_skills')
          .insert(skillsWithProjectId);

        if (skillsError) {
          console.error('Error creating project skills:', skillsError);
          // Don't fail the entire operation if skills creation fails
        }
      }

      return { data: project, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Update project
  async update(id: number, projectData: Partial<Project>): Promise<ApiResponse<Project>> {
    try {
      const { data: project, error } = await supabase
        .from('projects')
        .update(projectData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: project, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Update project with skills
  async updateWithSkills(id: number, projectData: CreateProjectRequest): Promise<ApiResponse<ProjectWithSkills>> {
    try {
      const { skills, ...projectInfo } = projectData;

      // Update project
      const { error: projectError } = await supabase
        .from('projects')
        .update(projectInfo)
        .eq('id', id);

      if (projectError) {
        return { data: null, error: projectError.message };
      }

      // Delete existing skills
      const { error: deleteError } = await supabase
        .from('project_skills')
        .delete()
        .eq('project_id', id);

      if (deleteError) {
        console.error('Error deleting existing skills:', deleteError);
      }

      // Create new skills if provided
      if (skills && skills.length > 0) {
        const skillsWithProjectId = skills.map(skill => ({
          skill_name: skill.skill_name,
          required_years: skill.required_years,
          is_required: skill.is_required,
          project_id: id
        }));

        const { error: skillsError } = await supabase
          .from('project_skills')
          .insert(skillsWithProjectId);

        if (skillsError) {
          console.error('Error creating project skills:', skillsError);
        }
      }

      // Fetch updated project with skills
      const { data: updatedProject, error: fetchError } = await supabase
        .from('projects')
        .select(`
          *,
          project_skills (*),
          project_assignments (*)
        `)
        .eq('id', id)
        .single();

      if (fetchError) {
        return { data: null, error: fetchError.message };
      }

      const projectWithSkills: ProjectWithSkills = {
        ...updatedProject,
        skills: updatedProject.project_skills || [],
        assignments: updatedProject.project_assignments || []
      };

      return { data: projectWithSkills, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Delete project
  async delete(id: number): Promise<ApiResponse<boolean>> {
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: true, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Add skill to project
  async addSkill(projectId: number, skill: Omit<ProjectSkill, 'id' | 'project_id' | 'created_at'>): Promise<ApiResponse<ProjectSkill>> {
    try {
      const { data, error } = await supabase
        .from('project_skills')
        .insert([{ ...skill, project_id: projectId }])
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Remove skill from project
  async removeSkill(skillId: number): Promise<ApiResponse<boolean>> {
    try {
      const { error } = await supabase
        .from('project_skills')
        .delete()
        .eq('id', skillId);

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: true, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  }
};

// Project Assignments Service
export const assignmentsService = {
  // Create assignment
  async create(assignment: Omit<ProjectAssignment, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<ProjectAssignment>> {
    try {
      const { data, error } = await supabase
        .from('project_assignments')
        .insert([assignment])
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Update assignment
  async update(id: number, assignment: Partial<ProjectAssignment>): Promise<ApiResponse<ProjectAssignment>> {
    try {
      const { data, error } = await supabase
        .from('project_assignments')
        .update(assignment)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Delete assignment
  async delete(id: number): Promise<ApiResponse<boolean>> {
    try {
      const { error } = await supabase
        .from('project_assignments')
        .delete()
        .eq('id', id);

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: true, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  }
};

// Matching Service
export const matchingService = {
  // Generate matching results between available engineers and open projects
  async getMatchingResults(filters?: MatchingFilters): Promise<ApiResponse<MatchingResult[]>> {
    try {
      // エンジニア・案件・アサインメントを並列取得（async-parallel ルール適用）
      const [engineersResult, projectsResult, assignmentsResult] = await Promise.all([
        supabase
          .from('engineers')
          .select(`
            *,
            engineer_skills (*),
            engineer_availability (*)
          `)
          .eq('availability_status', 'available')
          .order('id'),
        supabase
          .from('projects')
          .select(`
            *,
            project_skills (*),
            project_assignments (*)
          `)
          .in('status', ['pending', 'active'])
          .order('id'),
        supabase
          .from('project_assignments')
          .select('*')
          .order('created_at', { ascending: false })
      ]);

      if (engineersResult.error) {
        return { data: null, error: engineersResult.error.message };
      }

      if (projectsResult.error) {
        return { data: null, error: projectsResult.error.message };
      }

      if (assignmentsResult.error) {
        console.error('Error fetching assignments:', assignmentsResult.error);
      }

      const engineers = engineersResult.data;
      const projects = projectsResult.data;
      const allAssignments = assignmentsResult.data;

      // Generate matching results
      const matchingResults: MatchingResult[] = [];

      for (const engineer of engineers || []) {
        const engineerWithSkills: EngineerWithSkills = {
          ...engineer,
          skills: engineer.engineer_skills || [],
          availability: engineer.engineer_availability || []
        };

        for (const project of projects || []) {
          // Get fresh assignments for this project
          const projectAssignments = allAssignments?.filter(a => a.project_id === project.id) || [];
          
          const projectWithSkills: ProjectWithSkills = {
            ...project,
            skills: project.project_skills || [],
            assignments: projectAssignments
          };


          // Calculate match score and skill matches
          const matchResult = calculateMatch(engineerWithSkills, projectWithSkills);

          // Apply filters
          if (filters?.minMatchScore && matchResult.matchScore < filters.minMatchScore) {
            continue;
          }

          // Check for existing assignment to determine status
          const existingAssignment = projectWithSkills.assignments?.find(
            assignment => assignment.engineer_id === engineer.id
          );

          const status = existingAssignment?.status === 'proposed' ? 'sent' :
                        existingAssignment?.status === 'confirmed' ? 'accepted' :
                        existingAssignment?.status === 'cancelled' ? 'rejected' :
                        'pending';

          const matchingResult: MatchingResult = {
            id: `${engineer.id}-${project.id}`,
            engineer: engineerWithSkills,
            project: projectWithSkills,
            matchScore: matchResult.matchScore,
            skillMatches: matchResult.skillMatches,
            status,
            createdAt: new Date().toISOString().split('T')[0],
            assignment: existingAssignment
          };

          matchingResults.push(matchingResult);
        }
      }

      // Sort by match score descending
      matchingResults.sort((a, b) => b.matchScore - a.matchScore);

      // Apply status filter
      let filteredResults = matchingResults;
      if (filters?.status) {
        filteredResults = matchingResults.filter(result => result.status === filters.status);
      }

      return { data: filteredResults, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Send proposal to engineer for a specific project
  async sendProposal(engineerId: number, projectId: number, message?: string): Promise<ApiResponse<ProjectAssignment>> {
    try {
      // Check for existing assignment first
      const { data: existingAssignment, error: checkError } = await supabase
        .from('project_assignments')
        .select('*')
        .eq('project_id', projectId)
        .eq('engineer_id', engineerId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        return { data: null, error: checkError.message };
      }

      if (existingAssignment) {
        return { data: existingAssignment, error: null };
      }

      const assignment: Omit<ProjectAssignment, 'id' | 'created_at' | 'updated_at'> = {
        project_id: projectId,
        engineer_id: engineerId,
        status: 'proposed',
        notes: message || ''
      };

      const { data, error } = await supabase
        .from('project_assignments')
        .insert([assignment])
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: (error as Error).message };
    }
  },

  // Update proposal status (accept or reject)
  async updateProposalStatus(engineerId: number, projectId: number, status: 'confirmed' | 'cancelled', notes?: string): Promise<ApiResponse<ProjectAssignment>> {
    try {

      // First check if the assignment exists
      const { error: checkError } = await supabase
        .from('project_assignments')
        .select('*')
        .eq('project_id', projectId)
        .eq('engineer_id', engineerId)
        .eq('status', 'proposed')
        .single();

      if (checkError) {
        console.error('Error finding existing assignment:', checkError);
        return { data: null, error: `既存の提案が見つかりません: ${checkError.message}` };
      }


      const { data, error } = await supabase
        .from('project_assignments')
        .update({ 
          status,
          notes: notes || ''
        })
        .eq('project_id', projectId)
        .eq('engineer_id', engineerId)
        .eq('status', 'proposed')
        .select()
        .single();

      if (error) {
        console.error('Error updating assignment:', error);
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Exception in updateProposalStatus:', error);
      return { data: null, error: (error as Error).message };
    }
  }
};

// Helper function to calculate match score between engineer and project
function calculateMatch(engineer: EngineerWithSkills, project: ProjectWithSkills) {
  const projectSkills = project.skills || [];
  const engineerSkills = engineer.skills || [];

  if (projectSkills.length === 0) {
    return { matchScore: 0, skillMatches: [] };
  }

  // エンジニアスキルをMapに変換してO(1)ルックアップ（js-index-maps ルール適用）
  const engineerSkillMap = new Map(
    engineerSkills.map(es => [es.skill_name.toLowerCase(), es])
  );

  const skillMatches = projectSkills.map(projectSkill => {
    const engineerSkill = engineerSkillMap.get(projectSkill.skill_name.toLowerCase());

    const projectRequiredYears = projectSkill.required_years || 0;
    const engineerYears = engineerSkill?.years_experience || 0;

    return {
      skill: projectSkill.skill_name,
      required: projectSkill.is_required,
      engineerYears: engineerYears,
      projectRequiredYears: projectRequiredYears,
      match: engineerYears >= projectRequiredYears
    };
  });

  // Calculate match score
  const totalSkills = skillMatches.length;
  const requiredSkills = skillMatches.filter(sm => sm.required);
  const matchedRequiredSkills = requiredSkills.filter(sm => sm.match);

  // If not all required skills are matched, reduce score significantly
  if (requiredSkills.length > 0 && matchedRequiredSkills.length < requiredSkills.length) {
    const requiredMatchRatio = matchedRequiredSkills.length / requiredSkills.length;
    const matchScore = Math.round(requiredMatchRatio * 60); // Max 60% if missing required skills
    return { matchScore, skillMatches };
  }

  // Calculate score based on all skill matches
  const matchedSkills = skillMatches.filter(sm => sm.match).length;
  const baseScore = Math.round((matchedSkills / totalSkills) * 100);

  // Bonus for experience and location match
  let bonusScore = 0;
  
  // Experience bonus
  if (engineer.experience_years && engineer.experience_years >= 3) {
    bonusScore += 5;
  }
  
  // Location bonus (if both have location and they match)
  if (engineer.location && project.location) {
    const engineerPrefecture = engineer.location.split('県')[0];
    const projectPrefecture = project.location.split('県')[0];
    if (engineerPrefecture === projectPrefecture || 
        engineer.location.includes(projectPrefecture) || 
        project.location.includes(engineerPrefecture)) {
      bonusScore += 5;
    }
  }

  // Remote work bonus
  if (project.remote_work && engineer.remote_work_preference) {
    bonusScore += 3;
  }

  const finalScore = Math.min(100, baseScore + bonusScore);
  
  return { matchScore: finalScore, skillMatches };
}